// ── 품목별 식재료 사용량 집계 ──
// transactions.lineItems + purchaseLog.items 통합 → 품목별 누적 통계

/**
 * 품목명 정규화 — 공백·괄호·단위 제거
 */
export function normalizeItemName(s) {
  if (!s) return '';
  return String(s)
    .replace(/\(.+?\)/g, '')           // 괄호 안 제거
    .replace(/\d+\s*(kg|g|ml|l|개|매|장|봉|박스|팩|포|EA|ea)/gi, '')  // 단위·수량 제거
    .replace(/\s+/g, ' ')              // 공백 정리
    .trim()
    .toLowerCase();
}

/**
 * @param {Array} transactions  거래장부 (lineItems 포함)
 * @param {Array} purchaseLog   메모 영수증
 * @param {object} options      { monthKey?: 'YYYY-MM' 필터 }
 * @returns {Array<{key, displayName, totalQuantity, totalAmount, count, avgUnitPrice, lastUsed, sources}>}
 */
export function aggregateItemUsage(transactions = [], purchaseLog = [], options = {}) {
  const { monthKey } = options;
  const map = new Map();

  function addItem(rawItem, txDate, sourceLabel) {
    if (!rawItem || rawItem.included === false) return;
    const amt = Number(rawItem.amount) || 0;
    if (amt === 0) return;
    // 음수(반품/할인) 제외 — 사용량 집계는 양수만
    if (amt < 0) return;
    if (rawItem.isReturn || rawItem.isDiscount) return;

    const displayName = String(rawItem.itemName || '').trim();
    if (!displayName) return;
    const key = normalizeItemName(displayName);
    if (!key) return;

    if (!map.has(key)) {
      map.set(key, {
        key,
        displayName,
        totalQuantity: 0,
        totalAmount: 0,
        count: 0,
        unitPrices: [],
        lastUsed: txDate,
        sources: new Set(),
        isTaxFree: rawItem.isTaxFree || false,
        categories: new Map(),
      });
    }
    const it = map.get(key);
    const qty = Math.abs(Number(rawItem.quantity) || 1);
    it.totalQuantity += qty;
    it.totalAmount += amt;
    it.count += 1;
    if (rawItem.unitPrice && Number(rawItem.unitPrice) > 0) {
      it.unitPrices.push(Number(rawItem.unitPrice));
    } else if (qty > 0) {
      it.unitPrices.push(amt / qty);
    }
    if (!it.lastUsed || txDate > it.lastUsed) it.lastUsed = txDate;
    it.sources.add(sourceLabel);
    if (rawItem.isTaxFree) it.isTaxFree = true;

    // 카테고리 합산
    const catKey = `${rawItem.category?.major || 'etc'}::${rawItem.category?.sub || ''}`;
    it.categories.set(catKey, (it.categories.get(catKey) || 0) + amt);
  }

  // 1) 거래장부 lineItems
  for (const tx of transactions) {
    if (tx?.type !== 'expense') continue;
    if (monthKey && (!tx.date || !tx.date.startsWith(monthKey))) continue;
    if (!Array.isArray(tx.lineItems)) continue;
    for (const li of tx.lineItems) {
      addItem(li, tx.date, '거래장부');
    }
  }

  // 2) purchaseLog items (메모 영수증)
  for (const log of purchaseLog) {
    if (monthKey && (!log.date || !log.date.startsWith(monthKey))) continue;
    if (!Array.isArray(log.items)) continue;
    for (const li of log.items) {
      addItem(li, log.date, '메모');
    }
  }

  return Array.from(map.values()).map(it => ({
    key: it.key,
    displayName: it.displayName,
    totalQuantity: it.totalQuantity,
    totalAmount: it.totalAmount,
    count: it.count,
    avgUnitPrice: it.unitPrices.length > 0
      ? Math.round(it.unitPrices.reduce((s, v) => s + v, 0) / it.unitPrices.length)
      : 0,
    minUnitPrice: it.unitPrices.length > 0 ? Math.round(Math.min(...it.unitPrices)) : 0,
    maxUnitPrice: it.unitPrices.length > 0 ? Math.round(Math.max(...it.unitPrices)) : 0,
    lastUsed: it.lastUsed,
    sources: Array.from(it.sources),
    isTaxFree: it.isTaxFree,
  })).sort((a, b) => b.totalAmount - a.totalAmount);
}

/**
 * 특정 품목의 월별 사용 추이
 */
export function monthlyTrend(transactions = [], purchaseLog = [], itemKey) {
  if (!itemKey) return [];
  const map = new Map();

  function add(li, date, sourceLabel) {
    if (!li || li.included === false) return;
    const amt = Number(li.amount) || 0;
    if (amt <= 0 || li.isReturn || li.isDiscount) return;
    const k = normalizeItemName(li.itemName);
    if (k !== itemKey) return;
    const mk = (date || '').slice(0, 7);
    if (!mk) return;
    if (!map.has(mk)) map.set(mk, { month: mk, amount: 0, quantity: 0, count: 0 });
    const m = map.get(mk);
    m.amount += amt;
    m.quantity += Math.abs(Number(li.quantity) || 1);
    m.count += 1;
  }

  for (const tx of transactions) {
    if (tx?.type !== 'expense' || !Array.isArray(tx.lineItems)) continue;
    for (const li of tx.lineItems) add(li, tx.date, '거래장부');
  }
  for (const log of purchaseLog) {
    if (!Array.isArray(log.items)) continue;
    for (const li of log.items) add(li, log.date, '메모');
  }

  return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
}
